import type { Locale } from '../content/types';

export type RouteKind = 'home' | 'landing' | 'legal-notice' | 'data-protection' | 'not-found';

export type RouteDescriptor = {
  kind: RouteKind;
  locale: Locale;
  path: string;
};

export const localizedPaths = {
  landing: {
    en: '/en/',
    de: '/de/',
  },
  legalNotice: {
    en: '/en/legal-notice/',
    de: '/de/impressum/',
  },
  dataProtection: {
    en: '/en/data-protection/',
    de: '/de/datenschutz/',
  },
} as const satisfies Record<string, Record<Locale, string>>;

export const requiredSitemapRoutes = [
  localizedPaths.landing.en,
  localizedPaths.landing.de,
  localizedPaths.legalNotice.en,
  localizedPaths.dataProtection.en,
  localizedPaths.legalNotice.de,
  localizedPaths.dataProtection.de,
] as const;

export const prerenderRoutes = [
  '/',
  localizedPaths.landing.en,
  localizedPaths.landing.de,
  localizedPaths.legalNotice.en,
  localizedPaths.dataProtection.en,
  localizedPaths.legalNotice.de,
  localizedPaths.dataProtection.de,
  '/404/',
] as const;

export const sectionIds = {
  services: 'services',
  solutions: 'solutions',
  process: 'process',
  experience: 'experience',
  faq: 'faq',
  contact: 'contact',
} as const;

export const legacyRedirects = {
  '/about': `${localizedPaths.landing.en}#${sectionIds.experience}`,
  '/contact': `${localizedPaths.landing.en}#${sectionIds.contact}`,
} as const;

export function stripTrailingSlash(pathname: string): string {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function normalizePath(pathname: string): string {
  if (pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

export function getLocaleFromPath(pathname: string): Locale {
  return pathname.startsWith('/de') ? 'de' : 'en';
}

export function getRouteKind(pathname: string): RouteKind {
  const normalized = normalizePath(pathname);

  if (normalized === '/') {
    return 'home';
  }

  if (normalized === localizedPaths.legalNotice.en || normalized === localizedPaths.legalNotice.de) {
    return 'legal-notice';
  }

  if (
    normalized === localizedPaths.dataProtection.en ||
    normalized === localizedPaths.dataProtection.de
  ) {
    return 'data-protection';
  }

  if (normalized === localizedPaths.landing.en || normalized === localizedPaths.landing.de) {
    return 'landing';
  }

  return 'not-found';
}

export function getEquivalentPath(pathname: string, targetLocale: Locale, hash = ''): string {
  const normalized = normalizePath(pathname);
  const kind = getRouteKind(normalized);

  if (kind === 'legal-notice') {
    return `${localizedPaths.legalNotice[targetLocale]}${hash}`;
  }

  if (kind === 'data-protection') {
    return `${localizedPaths.dataProtection[targetLocale]}${hash}`;
  }

  if (kind === 'not-found') {
    return targetLocale === 'de' ? `${localizedPaths.landing.de}${hash}` : `${localizedPaths.landing.en}${hash}`;
  }

  return `${localizedPaths.landing[targetLocale]}${hash}`;
}

export function getCanonicalPath(kind: RouteKind, locale: Locale): string {
  if (kind === 'legal-notice') {
    return localizedPaths.legalNotice[locale];
  }

  if (kind === 'data-protection') {
    return localizedPaths.dataProtection[locale];
  }

  if (kind === 'not-found') {
    return '/404/';
  }

  return localizedPaths.landing[locale];
}
